import { tv } from "tailwind-variants"
import { type VariantProps } from "tailwind-variants"
import { interactiveVariants } from "./interactive"

export type TextColorVariantProps = VariantProps<typeof textColorVariants>
export type BorderColorVariantProps = VariantProps<typeof borderColorVariants>

// Define all color names as a string literal type
export type Color =
  | "transparent"
  | "current"
  | "inherit"
  | "white"
  | "black"
  // Text colors
  | "text-primary"
  | "text-secondary"
  | "text-tertiary"
  // Background colors
  | "bg-app"
  | "bg-primary"
  | "bg-secondary"
  | "bg-additional-1"
  | "bg-additional-2"
  | "bg-additional-3"
  | "bg-contrast-1"
  | "bg-contrast-2"
  // Transparent variants
  | "bg-primary-transparent"
  | "bg-secondary-transparent"
  | "bg-additional-1-transparent"
  | "bg-additional-2-transparent"
  | "bg-additional-3-transparent"
  | "bg-contrast-1-transparent"
  | "bg-contrast-2-transparent"
  // Border colors
  | "border-1"
  | "border-2"
  | "border-3"
  | "border-special-inset-1"
  | "border-1-transparent"
  | "border-2-transparent"
  | "border-3-transparent"
  // Frosted colors
  | "frosted-1"
  | "frosted-2"
  | "frosted-3"
  | "frosted-4"
  | "frosted-5"
  // Overlay
  | "overlay"
  // Rarity colors
  | "legendary"
  | "legendary-transparent"
  | "epic"
  | "epic-transparent"
  | "rare"
  | "rare-transparent"
  // Blue colors
  | "blue-1"
  | "blue-2"
  | "blue-3"
  | "blue-4"
  | "blue-5"
  | "link"
  // Semantic colors
  | "success"
  | "success-transparent-1"
  | "success-transparent-2"
  | "warning"
  | "warning-transparent-1"
  | "warning-transparent-2"
  | "caution"
  | "caution-transparent-1"
  | "caution-transparent-2"
  | "error"
  | "error-transparent-1"
  | "error-transparent-2"

// Create a mapped type for text color classes
type TextColorMap = {
  [K in Color]: `text-${K}`
}

type BorderColorMap = {
  [K in Color]: `border-${K}`
}

// Create the text color object with all variants
const textColor: TextColorMap = {
  transparent: "text-transparent",
  current: "text-current",
  inherit: "text-inherit",
  white: "text-white",
  black: "text-black",
  // Text colors
  "text-primary": "text-text-primary",
  "text-secondary": "text-text-secondary",
  "text-tertiary": "text-text-tertiary",
  // Background colors
  "bg-app": "text-bg-app",
  "bg-primary": "text-bg-primary",
  "bg-secondary": "text-bg-secondary",
  "bg-additional-1": "text-bg-additional-1",
  "bg-additional-2": "text-bg-additional-2",
  "bg-additional-3": "text-bg-additional-3",
  "bg-contrast-1": "text-bg-contrast-1",
  "bg-contrast-2": "text-bg-contrast-2",
  // Transparent variants
  "bg-primary-transparent": "text-bg-primary-transparent",
  "bg-secondary-transparent": "text-bg-secondary-transparent",
  "bg-additional-1-transparent": "text-bg-additional-1-transparent",
  "bg-additional-2-transparent": "text-bg-additional-2-transparent",
  "bg-additional-3-transparent": "text-bg-additional-3-transparent",
  "bg-contrast-1-transparent": "text-bg-contrast-1-transparent",
  "bg-contrast-2-transparent": "text-bg-contrast-2-transparent",
  // Border colors
  "border-1": "text-border-1",
  "border-2": "text-border-2",
  "border-3": "text-border-3",
  "border-special-inset-1": "text-border-special-inset-1",
  "border-1-transparent": "text-border-1-transparent",
  "border-2-transparent": "text-border-2-transparent",
  "border-3-transparent": "text-border-3-transparent",
  // Frosted colors
  "frosted-1": "text-frosted-1",
  "frosted-2": "text-frosted-2",
  "frosted-3": "text-frosted-3",
  "frosted-4": "text-frosted-4",
  "frosted-5": "text-frosted-5",
  // Overlay
  overlay: "text-overlay",
  // Rarity colors
  legendary: "text-legendary",
  "legendary-transparent": "text-legendary-transparent",
  epic: "text-epic",
  "epic-transparent": "text-epic-transparent",
  rare: "text-rare",
  "rare-transparent": "text-rare-transparent",
  // Blue colors
  "blue-1": "text-blue-1",
  "blue-2": "text-blue-2",
  "blue-3": "text-blue-3",
  "blue-4": "text-blue-4",
  "blue-5": "text-blue-5",
  link: "text-link",
  // Semantic colors
  success: "text-success",
  "success-transparent-1": "text-success-transparent-1",
  "success-transparent-2": "text-success-transparent-2",
  warning: "text-warning",
  "warning-transparent-1": "text-warning-transparent-1",
  "warning-transparent-2": "text-warning-transparent-2",
  caution: "text-caution",
  "caution-transparent-1": "text-caution-transparent-1",
  "caution-transparent-2": "text-caution-transparent-2",
  error: "text-error",
  "error-transparent-1": "text-error-transparent-1",
  "error-transparent-2": "text-error-transparent-2",
}

export const textColorVariants = tv({
  variants: {
    color: {
      ...textColor,
      "interactive-primary-styles": interactiveVariants({
        text: "primary",
      }),
      "interactive-secondary-styles": interactiveVariants({
        text: "secondary",
      }),
    },
  },
})

const borderColor: BorderColorMap = {
  transparent: "border-transparent",
  current: "border-current",
  inherit: "border-inherit",
  white: "border-white",
  black: "border-black",
  // Text colors
  "text-primary": "border-text-primary",
  "text-secondary": "border-text-secondary",
  "text-tertiary": "border-text-tertiary",
  // Background colors
  "bg-app": "border-bg-app",
  "bg-primary": "border-bg-primary",
  "bg-secondary": "border-bg-secondary",
  "bg-additional-1": "border-bg-additional-1",
  "bg-additional-2": "border-bg-additional-2",
  "bg-additional-3": "border-bg-additional-3",
  "bg-contrast-1": "border-bg-contrast-1",
  "bg-contrast-2": "border-bg-contrast-2",
  // Transparent variants
  "bg-primary-transparent": "border-bg-primary-transparent",
  "bg-secondary-transparent": "border-bg-secondary-transparent",
  "bg-additional-1-transparent": "border-bg-additional-1-transparent",
  "bg-additional-2-transparent": "border-bg-additional-2-transparent",
  "bg-additional-3-transparent": "border-bg-additional-3-transparent",
  "bg-contrast-1-transparent": "border-bg-contrast-1-transparent",
  "bg-contrast-2-transparent": "border-bg-contrast-2-transparent",
  // Border colors
  "border-1": "border-border-1",
  "border-2": "border-border-2",
  "border-3": "border-border-3",
  "border-special-inset-1": "border-border-special-inset-1",
  "border-1-transparent": "border-border-1-transparent",
  "border-2-transparent": "border-border-2-transparent",
  "border-3-transparent": "border-border-3-transparent",
  // Frosted colors
  "frosted-1": "border-frosted-1",
  "frosted-2": "border-frosted-2",
  "frosted-3": "border-frosted-3",
  "frosted-4": "border-frosted-4",
  "frosted-5": "border-frosted-5",
  // Overlay
  overlay: "border-overlay",
  // Rarity colors
  legendary: "border-legendary",
  "legendary-transparent": "border-legendary-transparent",
  epic: "border-epic",
  "epic-transparent": "border-epic-transparent",
  rare: "border-rare",
  "rare-transparent": "border-rare-transparent",
  // Blue colors
  "blue-1": "border-blue-1",
  "blue-2": "border-blue-2",
  "blue-3": "border-blue-3",
  "blue-4": "border-blue-4",
  "blue-5": "border-blue-5",
  link: "border-link",
  // Semantic colors
  success: "border-success",
  "success-transparent-1": "border-success-transparent-1",
  "success-transparent-2": "border-success-transparent-2",
  warning: "border-warning",
  "warning-transparent-1": "border-warning-transparent-1",
  "warning-transparent-2": "border-warning-transparent-2",
  caution: "border-caution",
  "caution-transparent-1": "border-caution-transparent-1",
  "caution-transparent-2": "border-caution-transparent-2",
  error: "border-error",
  "error-transparent-1": "border-error-transparent-1",
  "error-transparent-2": "border-error-transparent-2",
}

export const borderColorVariants = tv({
  variants: {
    color: borderColor,
  },
})
